<template>
  <div>
    <template v-for="key in dialogs" :key="key">
      <component
        :is="modals[key]"
        :data="dialogsData[key]"
        @close="onClose(key)"
      ></component>
    </template>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import _ from 'lodash';
import ShareReportDialog from './share-report-dialog.vue';
import ExportCSV from './export-csv.vue';
import CampaignDialog from '@/views/reports/forms/campaign/campaign-dialog.vue';
import OfferDialog from '@/views/reports/forms/offer/offer-dialog.vue';
import LandingDialog from '@/views/reports/forms/landing/landing-dialog.vue';
import FlowDialog from '@/views/reports/forms/flow/flow-dialog.vue';
import TrafficSourceDialog from '@/views/reports/forms/traffic_sources/traffic-source-dialog.vue';
import ChangeWorkspace from './change-workspace-dialog.vue';
import AffiliateNetworkDialog from '@/views/reports/forms/affiliate_network/affiliate-network-dialog.vue';
import UpdateCost from '@/views/reports/forms/campaign/cost/update-cost.vue';
import CampaignLinks from '@/views/reports/forms/campaign/links/links-form.vue';
import LpPixel from '@/views/reports/forms/campaign/links/lp-pixel/lp-pixel-form.vue';
import TrackConversionsWithNoClickIds from '@/views/reports/forms/campaign/links/track-conversions-with-no-click-ids.vue';
import ColumnsSettingsDialog from '@/views/reports/pickers/cores/columns-settings-dialog/columns-settings-dialog.vue';
import AddNewColumnDialog from '@/views/reports/pickers/cores/add-new-column-dialog.vue';
import ImportDialog from '@/components/dialogs/import-dialog.vue';
import AffiliateNetworkFromTemplate from '@/views/reports/forms/affiliate_network/template.vue';
import TrafficSourceFromTemplate from '@/views/reports/forms/traffic_sources/template.vue';
import AddCustomCloakingDomainDialog from '@/views/settings/domains/forms/cloaking/add-custom-cloaking-domain-dialog.vue';
import AddCustomTrackingDomainDialog from '@/views/settings/domains/forms/tracking/add-custom-tracking-domain-dialog.vue';
import BulkEditCampaign from '@/views/reports/forms/campaign/bulk-edit.vue';
import BulkEditOffer from '@/views/reports/forms/offer/bulk-edit.vue';
import BulkEditLanding from '@/views/reports/forms/landing/bulk-edit.vue';
import ExchangeRatesDialog from '@/views/settings/general/forms/forms/exchange-rates-dialog.vue';
import ChangePassword from '@/views/settings/security/forms/password/change-password.vue';
import CreateApiKeys from '@/views/settings/security/forms/apikeys/create-api-keys.vue';
import EnableSecondFactor from '@/views/settings/security/forms/second-factor/enable.vue';
import DisableSecondFactor from '@/views/settings/security/forms/second-factor/disable.vue';
import ChangePlanDialog from '@/views/settings/subscription/forms/change-plan/change-plan.vue';
import PromoCode from '@/views/settings/subscription/forms/apply-promo/apply-promo.vue';
import UpdatePaymentMethodDialog from '@/views/settings/subscription/forms/update-payment-method/update-payment-method.vue';
import UpdateBillingInfoDialog from '@/views/settings/subscription/forms/update-billing-info/update-billing-info.vue';
import PaymentInformation from '@/views/settings/referral-program/forms/referral-balance/payment-information.vue';
import CreateCustomConversions from '@/views/settings/custom-conversions/forms/create-custom-conversions.vue';
import CreateWorkspaceDialog from '@/views/settings/multi-user-access/workspaces/forms/create-workspace-dialog.vue';
import CreateMember from '@/views/settings/multi-user-access/user-manager/forms/create-member-dialog.vue';
import CreateLinkDialog from '@/components/dialogs/create-link-dialog.vue';
import ItemsInWorkspace from '@/views/settings/multi-user-access/workspaces/forms/items-in-workspace.vue';
import GenerateLinks from '@/views/settings/custom-conversions/forms/generate-links.vue';
import EditAccessPermissions from '@/views/settings/multi-user-access/workspaces/forms/edit-access-permissions.vue';
import CreateRule from '@/views/reports/forms/campaign/rules/rules-form.vue';
import CreateIntegrationDialog from '@/views/settings/integrations/custom-integrations/forms/create-integration-dialog.vue';
import CreateIntegrationDialogFacebook from '@/views/settings/integrations/custom-integrations/forms/create-integration-dialog-facebook.vue';
import ChooseIntegrationDialog from '@/views/settings/integrations/custom-integrations/forms/choose-integration-dialog.vue';
import AddBotDetectionRules from '@/views/settings/bot-filters/forms/add-bot-detection-rules.vue';
import TwoFactorBackupCodeDialog from '@/views/pages/two-factor/two-factor-backup-code-dialog.vue';
import TemporaryPasswordDialog from '@/views/settings/temporary-password-dialog.vue';
import BookmarkAddDialog from '@/components/bookmark/bookmark-add-menu.vue';
import ManageAddon from './manage-addon/manage-addon.vue';
import CustomConversionsSending from './custom-conversions-sending.vue';
import ReportTotalSettings from './report-totals-settings.vue';
import ResendPostbacks from './resend-postbacks.vue';
import ConfirmDialog from '@/components/dialogs/confirm-dialog.vue';
import { $emitter } from '@/services/emitter';

const modals = {
  ShareReportDialog,
  ExportCSV,
  CampaignDialog,
  OfferDialog,
  LandingDialog,
  FlowDialog,
  TrafficSourceDialog,
  ChangeWorkspace,
  ManageAddon,
  AffiliateNetworkDialog,
  UpdateCost,
  CampaignLinks,
  LpPixel,
  TrackConversionsWithNoClickIds,
  ColumnsSettingsDialog,
  AddNewColumnDialog,
  ImportDialog,
  AffiliateNetworkFromTemplate,
  TrafficSourceFromTemplate,
  AddCustomCloakingDomainDialog,
  AddCustomTrackingDomainDialog,
  BulkEditCampaign,
  BulkEditOffer,
  BulkEditLanding,
  ExchangeRatesDialog,
  ChangePassword,
  CreateApiKeys,
  EnableSecondFactor,
  DisableSecondFactor,
  ChangePlanDialog,
  PromoCode,
  UpdatePaymentMethodDialog,
  UpdateBillingInfoDialog,
  PaymentInformation,
  CreateCustomConversions,
  CreateWorkspaceDialog,
  CreateMember,
  CreateLinkDialog,
  ItemsInWorkspace,
  GenerateLinks,
  EditAccessPermissions,
  CreateRule,
  CreateIntegrationDialog,
  CreateIntegrationDialogFacebook,
  ChooseIntegrationDialog,
  AddBotDetectionRules,
  TwoFactorBackupCodeDialog,
  TemporaryPasswordDialog,
  BookmarkAddDialog,
  CustomConversionsSending,
  ReportTotalSettings,
  ResendPostbacks,
  ConfirmDialog,
} as const;

type ModalName = keyof typeof modals;

const dialogsData = ref<Record<string, unknown>>({});
const dialogs = ref<ModalName[]>([]);

const onBusOpenDialog = ({ name, data }: { name: ModalName; data?: unknown }) => {
  if (!dialogs.value.includes(name)) {
    if (data !== undefined) {
      dialogsData.value[name] = data;
    }
    dialogs.value.push(name);
  }
};

const onBusCloseAllDialogs = () => {
  dialogs.value = [];
};

const onClose = (name: ModalName) => {
  dialogsData.value = _.omit(dialogsData.value, name);
  dialogs.value = dialogs.value.slice(0, -1);
};

onMounted(() => {
  $emitter.on('open-dialog', onBusOpenDialog);
  $emitter.on('close-all-dialogs', onBusCloseAllDialogs);
});

onBeforeUnmount(() => {
  $emitter.off('open-dialog', onBusOpenDialog);
  $emitter.off('close-all-dialogs', onBusCloseAllDialogs);
});
</script>

<style lang="scss" scoped>
</style>
